<!-- NoteGeneralForm.vue -->
<script setup>
import { ref, reactive, computed, watch, onMounted, onUnmounted, nextTick } from "vue";
import { useRouter } from "vue-router";
import debounce from "lodash.debounce";

// Stores
import { useNoteGeneralStore } from "@/stores/useNoteGeneralStore";
import { useUserStore } from "@/stores/useUserStore";
import { useNoteStore } from "@/stores/useNoteStore";
import { useActivityStore } from "@/stores/useActivityStore";
// ── NOVO: guard de permissão por vínculo Membro-Titular ───────────────────────
import { useManagedAccountStore } from "@/stores/useManagedAccountStore";

// Paginação
import PaginationButtons from "../Generics/PaginationButtons.vue";
import PaginationInfo from "../Generics/PaginationInfo.vue";

// Modal
import NoteCardModal from "@/components/Note/NoteCardModal.vue";
import TagCardModal from "@/components/Tag/TagCardModal.vue";
import UserCardModal from "@/components/User/UserCardModal.vue";
import EditNoteForm from "@/components/Note/EditNoteForm.vue";
import EditNoteObservationForm from "@/components/Note/EditNoteObservationForm.vue";
import MarkObservationAsRead from "@/components/Note/MarkObservationAsRead.vue";
import DeleteNoteForm from "@/components/Note/DeleteNoteForm.vue";
import PermissionView from "@/components/Generics/PermissionView.vue";
import BatchDeleteNoteForm from "@/components/Note/BatchDeleteNoteForm.vue";
import NoteAttachmentModal from "@/components/Note/NoteAttachmentModal.vue";

// Avatar/API helper
import api from "@/services/api";
import { normalizeForSearch } from "@/utils/stringUtils";
import ShareNotePopover from "@/components/Note/ShareNotePopover.vue";
import { useNoteShare } from "@/composables/useNoteShare";

// Links da nota: extração, previews (imagem, YouTube, documento, site) e anexos
import {
  linkifyBody,
  extractUrls,
  getNoteAttachments,
  hasAttachment,
} from "@/utils/noteLinks";
import { useLinkPreviews } from "@/composables/useLinkPreviews";

// ─────────────────────────────────────────────────────────────────────────────
// STORES & ROLES
// ─────────────────────────────────────────────────────────────────────────────
const store = useNoteGeneralStore();
const userStore = useUserStore();
const noteStore = useNoteStore();
const activityStore = useActivityStore();
const router = useRouter();
// ── NOVO ──────────────────────────────────────────────────────────────────────
const managedAccountStore = useManagedAccountStore();

const isSys = computed(() => userStore.currentUser?.userName === "Sys");

// ── NOVO: permissões efetivas do vínculo para notas ───────────────────────────
const memberNotePermissions = computed(
  () => managedAccountStore.currentMemberPermissions?.notes ?? null
);
const memberCanEditNote = computed(
  () => memberNotePermissions.value === null || memberNotePermissions.value.edit
);
const memberCanDeleteNote = computed(
  () =>
    memberNotePermissions.value === null || memberNotePermissions.value.delete
);

// compartilhamento de nota
const {
  isSharePopoverOpen,
  absoluteShareUrl,
  isGenerating,
  isCopied,
  isRevoking,
  shareError,
  shareNoteId,
  openSharePopover,
  generateLink,
  closeSharePopover,
  copyLink,
  shareWhatsApp,
  shareEmail,
  shareNative,
  revokeLink,
} = useNoteShare();

const sharingNoteTitle = computed(() => {
  if (!shareNoteId.value) return "";
  const n = store.filteredNotes.find((n) => n.noteId === shareNoteId.value);
  return n?.title ?? "";
});

const isShareOwner = computed(() => {
  if (!shareNoteId.value) return false;
  const n = store.filteredNotes.find((n) => n.noteId === shareNoteId.value);
  return n?.createdByUserId === userStore.currentUser?.userId;
});

// ─────────────────────────────────────────────────────────────────────────────
// PAGINAÇÃO
// ─────────────────────────────────────────────────────────────────────────────
const currentPage = ref(1);
const pageSize = ref(24);

// ✅ PATCH: controla o estado de carregamento das notas
const isLoadingNotes = ref(false);

// ✅ PATCH: evita que uma requisição antiga desligue o loading de uma requisição nova
const fetchNotesRequestId = ref(0);

watch(pageSize, (value) => {
  if (value === "" || value === null || typeof value === "undefined") {
    pageSize.value = 0;
    return;
  }
  let v = Number(value);
  if (isNaN(v)) {
    pageSize.value = 0;
    return;
  }
  if (v < 0) v = 0;
  if (v > 100) v = 100;
  if (v !== value) {
    pageSize.value = v;
    return;
  }
  // Fora da página 1, o watcher de currentPage é quem refaz a busca — chamar
  // fetchNotes() aqui também dispararia duas requisições iguais.
  if (currentPage.value !== 1) {
    currentPage.value = 1;
    return;
  }
  fetchNotes();
});

// ─────────────────────────────────────────────────────────────────────────────
// MODAIS
// ─────────────────────────────────────────────────────────────────────────────
const showNoteCardModal = ref(false);
const showTagCardModal = ref(false);
const showUserCardModal = ref(false);
const showEditNoteModal = ref(false);
const showEditObservationModal = ref(false);
const showReadObservationModal = ref(false);
const showDeleteNoteModal = ref(false);
const showPermissionViewModal = ref(false);
const showBatchDeleteModal = ref(false);
const isBatchActionsOpen = ref(false);

const selectedNoteId = ref(null);
const selectedTagId = ref(null);
const selectedUserId = ref(null);
const selectedNoteForObservation = ref(null);
const editInitialStep = ref(1);

// Evita busca duplicada quando o NoteCardModal é fechado após exclusão.
const noteCardClosedAfterDelete = ref(false);

// ✅ FIX: marca se a nota foi editada enquanto o card estava aberto.
// (Exclusão tem caminho próprio via handleNoteCardRefresh; edição mantém o card aberto.)
const noteCardChanged = ref(false);

function openNoteCardModal(noteId) {
  if (!noteId) return;
  selectedNoteId.value = noteId;
  // ✅ FIX: abertura não é mudança — zera o flag de alteração
  noteCardChanged.value = false;
  showNoteCardModal.value = true;
}

// ✅ FIX: disparado pelo evento "note-updated" do NoteCardModal (edição).
// O card permanece aberto; só marcamos que houve alteração para recarregar ao fechar.
function handleNoteCardUpdated() {
  noteCardChanged.value = true;
}

async function closeNoteCardModal() {
  showNoteCardModal.value = false;
  selectedNoteId.value = null;

  // ✅ FIX: se o fechamento veio após exclusão, o fetch já foi disparado por
  // handleNoteCardRefresh — apenas zera os flags e sai (evita busca dupla).
  if (noteCardClosedAfterDelete.value) {
    noteCardClosedAfterDelete.value = false;
    noteCardChanged.value = false;
    return;
  }

  // ✅ FIX: recarrega SOMENTE se houve edição no card.
  // Visualização pura (abrir e fechar sem mudar nada) NÃO recarrega.
  if (noteCardChanged.value) {
    noteCardChanged.value = false;
    await fetchNotes();
  }
}

async function handleNoteCardRefresh() {
  const deletedNoteId = selectedNoteId.value;

  noteCardClosedAfterDelete.value = true;
  // ✅ FIX: o caminho de exclusão recarrega aqui; zera o flag de edição para não duplicar
  noteCardChanged.value = false;
  showNoteCardModal.value = false;
  selectedNoteId.value = null;

  if (deletedNoteId) {
    store.selectedNoteIds = store.selectedNoteIds.filter(
      (id) => Number(id) !== Number(deletedNoteId)
    );
    noteStore.selectedNoteIds = noteStore.selectedNoteIds.filter(
      (id) => Number(id) !== Number(deletedNoteId)
    );
  }

  await fetchNotes();
}

function openTagCardModal(note, index) {
  if (!note) return;
  let tagId = null;
  if (Array.isArray(note.tagIds)) tagId = note.tagIds[index] ?? null;
  if (!tagId && typeof note.tagId !== "undefined") tagId = note.tagId;
  if (!tagId) return;
  selectedTagId.value = tagId;
  showTagCardModal.value = true;
}
function openUserCardModal(userId) {
  if (!userId) return;
  selectedUserId.value = userId;
  showUserCardModal.value = true;
}
function handleUserCardModalClose() {
  showUserCardModal.value = false;
  selectedUserId.value = null;
}

function closeTagCardModal() {
  showTagCardModal.value = false;
  selectedTagId.value = null;
}

function openEditNoteModal(note) {
  if (!note?.noteId) return;
  selectedNoteId.value = note.noteId;
  editInitialStep.value = 1;
  showEditNoteModal.value = true;
  openDropdownNoteId.value = null;
}
async function closeEditNoteModal() {
  showEditNoteModal.value = false;
  selectedNoteId.value = null;
  editInitialStep.value = 1;
  await fetchNotes();
}

function openEditObservationModal(note) {
  if (!note?.noteId) return;
  selectedNoteId.value = note.noteId;
  showEditObservationModal.value = true;
  openDropdownNoteId.value = null;
}
async function closeEditObservationModal() {
  showEditObservationModal.value = false;
  selectedNoteId.value = null;
  await fetchNotes();
}

function openReadObservationModal(note) {
  if (!note?.noteId) return;
  selectedNoteId.value = note.noteId;
  selectedNoteForObservation.value = note;
  showReadObservationModal.value = true;
  openDropdownNoteId.value = null;
}
async function closeReadObservationModal() {
  showReadObservationModal.value = false;
  selectedNoteId.value = null;
  selectedNoteForObservation.value = null;
  await fetchNotes();
}

function syncBatchSelectionToNoteStore() {
  noteStore.selectedNoteIds = [...store.selectedNoteIds];
}

function openDeleteNoteModal(note) {
  if (!note?.noteId) return;
  if (note?.activityId) activityStore.idActivity = note.activityId;
  noteStore.selectedNoteIds = [note.noteId];
  selectedNoteId.value = note.noteId;
  showDeleteNoteModal.value = true;
  openDropdownNoteId.value = null;
}
async function closeDeleteNoteModal() {
  showDeleteNoteModal.value = false;
  selectedNoteId.value = null;
  noteStore.selectedNoteIds = [];
  await fetchNotes();
}

function openPermissionViewModal(note) {
  if (!note?.noteId) return;
  selectedNoteId.value = note.noteId;
  showPermissionViewModal.value = true;
  openDropdownNoteId.value = null;
}
function closePermissionViewModal() {
  showPermissionViewModal.value = false;
  selectedNoteId.value = null;
}

function toggleBatchActionsDropdown() {
  if (store.selectedNoteIds.length === 0) return;
  isBatchActionsOpen.value = !isBatchActionsOpen.value;
}

function openBatchDeleteModal() {
  if (store.selectedNoteIds.length === 0) return;
  syncBatchSelectionToNoteStore();
  const firstSelectedNoteWithActivity = store.filteredNotes.find(
    (note) =>
      store.selectedNoteIds.includes(note.noteId) &&
      note.activityId !== null &&
      typeof note.activityId !== "undefined"
  );
  if (firstSelectedNoteWithActivity?.activityId)
    activityStore.idActivity = firstSelectedNoteWithActivity.activityId;
  showBatchDeleteModal.value = true;
  isBatchActionsOpen.value = false;
}
async function closeBatchDeleteModal() {
  showBatchDeleteModal.value = false;
  store.selectedNoteIds = [];
  noteStore.selectedNoteIds = [];
  await fetchNotes();
}

// ─────────────────────────────────────────────────────────────────────────────
// NAVEGAÇÃO
// ─────────────────────────────────────────────────────────────────────────────
async function goToContext(note) {
  try {
    await activityStore.openTextForAnnotation(note.textId, {
      preferActivityId: note.activityId,
    });
    // >>> Abre a atividade filtrada pelo DONO da nota clicada (não pelo usuário
    // que mais postou). NoteSection.loadInitialNotes consome este valor.
    noteStore.pendingContextOwnerUserId = note.createdByUserId ?? null;

    router.push({ name: "RightArea" });
  } catch (error) {
    console.error("❌ Erro ao abrir a nota no contexto:", error);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// TOAST
// ─────────────────────────────────────────────────────────────────────────────
const showToast = ref(false);
const toastMessage = ref("");
const toastType = ref("error");
const toastTimeoutId = ref(null);

function resetToastTimeout() {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
    toastTimeoutId.value = null;
  }, 3000);
}
function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

// ✅ FIX: toast de sucesso no nível do pai. Necessário porque, ao apagar uma nota
// pelo card, o card (e o DeleteConfirmModal com seu toast interno) é fechado
// imediatamente; logo o feedback de sucesso precisa ser exibido aqui.
function showSuccessToast(message) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

// ✅ FIX: trata notificações vindas do NoteCardModal (exclusão de nota).
// Só exibimos SUCESSO aqui — erros continuam a cargo do DeleteConfirmModal,
// que mantém o card aberto e já mostra o toast de erro (evita duplicidade).
function handleNoteCardNotify(payload) {
  if (!payload) return;
  if (payload.type === "success") {
    showSuccessToast(payload.message || "Operação realizada com sucesso.");
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// FILTROS
// ─────────────────────────────────────────────────────────────────────────────
const local = reactive({
  noteTitle: store.filters.noteTitle ?? "",
  noteBody: store.filters.noteBody ?? "",
  activityTitle: store.filters.activityTitle ?? "",
  textTitle: store.filters.textTitle ?? "",
  tag: store.filters.tag ?? "",
  source: store.filters.source ?? "",
  permissionGroupName: store.filters.permissionGroupName ?? "",
  permissionUserName: store.filters.permissionUserName ?? "",
  createdFrom: store.filters.createdFrom ?? "",
  createdTo: store.filters.createdTo ?? "",
  creatorId: store.filters.creatorId ?? "",
  creatorName: store.filters.creatorName ?? "",
  onlyWithBody: store.filters.onlyWithBody ?? false,
  onlyWithObservation: store.filters.onlyWithObservation ?? false,
  onlyDetachedNotes: store.filters.onlyDetachedNotes ?? false,
  // ✅ NOVO: espelha onlyMine da store
  onlyMine: store.filters.onlyMine ?? false,
  // ✅ NOVO: tipos de observação selecionados (multi-seleção)
  observationTypes: Array.isArray(store.filters.observationTypes)
    ? [...store.filters.observationTypes]
    : [],
  // ✅ NOVO: busca textual na observação
  observationSearch: store.filters.observationSearch ?? "",
  // ✅ NOVO: estado de leitura da observação ("" | "read" | "unread")
  observationReadState: store.filters.observationReadState ?? "",
});

const debouncedNumeric = debounce((field, value) => {
  const trimmed = String(value ?? "").trim();
  if (trimmed !== "" && (!/^\d+$/.test(trimmed) || Number(trimmed) <= 0))
    return;
  store.setFilter(field, trimmed);
}, 400);

const debouncedText = debounce((field, value) => {
  store.setFilter(field, String(value ?? "").trim());
}, 300);

watch(
  () => local.noteTitle,
  (v) => debouncedText("noteTitle", v)
);
watch(
  () => local.noteBody,
  (v) => debouncedText("noteBody", v)
);
watch(
  () => local.activityTitle,
  (v) => debouncedText("activityTitle", v)
);
watch(
  () => local.textTitle,
  (v) => debouncedText("textTitle", v)
);
watch(
  () => local.tag,
  (v) => debouncedText("tag", v)
);
watch(
  () => local.source,
  (v) => debouncedText("source", v)
);
watch(
  () => local.permissionGroupName,
  (v) => debouncedText("permissionGroupName", v)
);
watch(
  () => local.permissionUserName,
  (v) => debouncedText("permissionUserName", v)
);
watch(
  () => local.createdFrom,
  (v) => debouncedText("createdFrom", v)
);
watch(
  () => local.createdTo,
  (v) => debouncedText("createdTo", v)
);
watch(
  () => local.creatorId,
  (v) => debouncedNumeric("creatorId", v)
);
watch(
  () => local.creatorName,
  (v) => debouncedText("creatorName", v)
);
watch(
  () => local.onlyWithBody,
  (v) => store.setFilter("onlyWithBody", v)
);
watch(
  () => local.onlyWithObservation,
  (v) => store.setFilter("onlyWithObservation", v)
);
watch(
  () => local.onlyDetachedNotes,
  (v) => store.setFilter("onlyDetachedNotes", v)
);
// ✅ NOVO
watch(
  () => local.onlyMine,
  (v) => store.setFilter("onlyMine", v)
);
// ✅ NOVO: tipos de observação (array — watch profundo para captar marcar/desmarcar)
watch(
  () => local.observationTypes,
  (v) => store.setFilter("observationTypes", [...(v ?? [])]),
  { deep: true }
);
// ✅ NOVO: busca textual na observação (debounced, como os demais campos de texto)
watch(
  () => local.observationSearch,
  (v) => debouncedText("observationSearch", v)
);
// ✅ NOVO: estado de leitura — é clique em checkbox, aplica direto (sem debounce)
watch(
  () => local.observationReadState,
  (v) => store.setFilter("observationReadState", v)
);

watch(
  () => ({ ...store.filters }),
  () => {
    if (currentPage.value !== 1) {
      currentPage.value = 1;
    } else {
      fetchNotes();
    }
  },
  { deep: true }
);

function clearAllFilters() {
  Object.keys(local).forEach((k) => {
    if (typeof local[k] === "boolean") local[k] = false;
    else if (Array.isArray(local[k])) local[k] = [];
    else local[k] = "";
  });
  activeFilterKeys.value = [];
  addedInThisSession.value = [];
  isAddFilterOpen.value = false;
  store.clearFilters();
}

const hasActiveFilters = computed(() =>
  Object.entries(local).some(([, v]) => {
    if (typeof v === "boolean") return v === true;
    if (Array.isArray(v)) return v.length > 0;
    return String(v ?? "").trim() !== "";
  })
);

// ─────────────────────────────────────────────────────────────────────────────
// FILTROS AVANÇADOS (painel "Filtros ativos" + chips)
// ─────────────────────────────────────────────────────────────────────────────
// `short` + `verb` compõem o rótulo da chip (ex.: Tag contém "geografia").
const ADVANCED_FILTERS = [
  {
    key: "textTitle",
    label: "Título do texto",
    type: "text",
    placeholder: "ex: Dom Quixote…",
    short: "Título do texto",
    verb: "contém",
    icon: "fas fa-book",
  },
  {
    key: "source",
    label: "Fonte",
    type: "text",
    placeholder: "ex: p. 42…",
    short: "Fonte",
    verb: "contém",
    icon: "fas fa-link",
  },
  {
    key: "creatorName",
    label: "Criador",
    type: "text",
    placeholder: "ex: Maria…",
    short: "Criador",
    verb: "=",
    icon: "fas fa-user",
  },
  {
    key: "creatorId",
    label: "ID do criador",
    type: "numeric",
    placeholder: "ex: 7",
    short: "ID do criador",
    verb: "=",
    icon: "fas fa-id-badge",
  },
  {
    key: "createdRange",
    label: "Data de criação",
    type: "dateRange",
    fromKey: "createdFrom",
    toKey: "createdTo",
    short: "Data",
    icon: "fas fa-calendar-alt",
  },
  // Fecha a primeira coluna do menu "Adicionar filtro"
  {
    key: "tag",
    label: "Tag",
    type: "text",
    placeholder: "ex: dúvida…",
    short: "Tag",
    verb: "contém",
    icon: "fas fa-hashtag",
  },
  {
    key: "permissionGroupName",
    label: "Compartilhada com grupo",
    type: "text",
    placeholder: "ex: Crianças…",
    short: "Compartilhada com grupo",
    verb: "quoted",
    icon: "fas fa-users-cog",
  },
  {
    key: "permissionUserName",
    label: "Compartilhada com usuário",
    type: "text",
    placeholder: "ex: Maria…",
    short: "Compartilhada com usuário",
    verb: "quoted",
    icon: "fas fa-user-tag",
  },
  {
    key: "onlyWithBody",
    label: "Com conteúdo",
    type: "checkbox",
    short: "com conteúdo",
    icon: "fas fa-align-left",
  },
  {
    key: "onlyWithObservation",
    label: "Com observação",
    type: "checkbox",
    short: "com observação",
    icon: "fas fa-comment-dots",
  },
  {
    key: "observationTypes",
    label: "Observação",
    type: "observationType",
    searchKey: "observationSearch",
    readKey: "observationReadState",
    short: "Observação",
    placeholder: "Buscar na observação…",
    icon: "fas fa-clipboard-check",
  },
  {
    key: "onlyDetachedNotes",
    label: "Notas avulsas",
    type: "checkbox",
    short: "notas avulsas",
    icon: "fas fa-unlink",
  },
];

// ✅ NOVO: opções do filtro "Observação" (valores PascalCase = observationType da nota).
// "Observações" não tem opção própria: nenhum marcado + termo de busca preenchido
// = busca apenas na observação; sem termo = sem restrição por tipo.
const OBSERVATION_TYPE_OPTIONS = [
  { value: "Correct", label: "Acertos", color: "#22c55e" },
  { value: "Incorrect", label: "Erros", color: "#ef4444" },
];

// ✅ NOVO: estado de leitura da observação — quem marca como lida é o CRIADOR da
// nota (MarkObservationAsRead.vue). Grupo próprio, exclusivo entre si, porém
// INDEPENDENTE de Acertos/Erros: combina em AND. Espelha o filtro do NoteFilter.
const OBSERVATION_READ_OPTIONS = [
  { value: "read", label: "Lidas", color: "#3b82f6" },
  { value: "unread", label: "Não lidas", color: "#9ca3af" },
];

// Acertos/Erros são mutuamente exclusivos: marcar um substitui o outro.
function toggleObservationType(key, value) {
  const current = Array.isArray(local[key]) ? local[key] : [];
  local[key] = current.includes(value) ? [] : [value];
}

// Lidas/Não lidas também se substituem — juntas se anulariam.
function toggleObservationReadState(key, value) {
  local[key] = local[key] === value ? "" : value;
}

// Filtros da linha principal — não entram no dropdown, mas viram chip no painel.
const BASE_FILTERS = [
  {
    key: "noteTitle",
    type: "text",
    short: "Título da nota",
    verb: "contém",
    icon: "fas fa-heading",
  },
  {
    key: "noteBody",
    type: "text",
    short: "Conteúdo da nota",
    verb: "contém",
    icon: "fas fa-align-left",
  },
  {
    key: "activityTitle",
    type: "text",
    short: "Título da atividade",
    verb: "contém",
    icon: "fas fa-file-signature",
  },
  // Botão "Minhas Notas" — vira chip como qualquer outra condição de filtragem.
  {
    key: "onlyMine",
    type: "checkbox",
    short: "Minhas notas",
    icon: "fas fa-user",
  },
];
const ALL_FILTERS = [...BASE_FILTERS, ...ADVANCED_FILTERS];

const isAdvancedPanelOpen = ref(false);
const isAddFilterOpen = ref(false);
const addFilterMenuRef = ref(null);
const activeFilterKeys = ref([]);
const addedInThisSession = ref([]);

// Controles da linha principal — alvo do clique nas chips de filtro base.
const noteTitleInputRef = ref(null);
const noteBodyInputRef = ref(null);
const activityTitleInputRef = ref(null);
const myNotesButtonRef = ref(null);

// Duas colunas independentes: abrir um campo cresce só a coluna dele.
const addFilterColumns = computed(() => {
  const options = addFilterOptions.value;
  const half = Math.ceil(options.length / 2);
  return [options.slice(0, half), options.slice(half)];
});

const activeFilters = computed(() =>
  activeFilterKeys.value
    .map((key) => ADVANCED_FILTERS.find((f) => f.key === key))
    .filter(Boolean)
);

// Filtros ainda não adicionados — é o que o dropdown mostra ao ser aberto.
const remainingFilters = computed(() =>
  ADVANCED_FILTERS.filter((f) => !activeFilterKeys.value.includes(f.key))
);

// Um filtro adicionado continua listado enquanto o dropdown permanecer aberto,
// para que o campo de preenchimento fique visível ao lado do seu nome.
const addFilterOptions = computed(() =>
  ADVANCED_FILTERS.filter(
    (f) =>
      !activeFilterKeys.value.includes(f.key) ||
      addedInThisSession.value.includes(f.key)
  )
);

function isFilterAdded(key) {
  return activeFilterKeys.value.includes(key);
}

// Um filtro só vira chip quando tem valor — opção clicada e não preenchida não conta.
function hasFilterValue(filter) {
  if (!filter) return false;
  if (filter.type === "checkbox") return local[filter.key] === true;
  if (filter.type === "observationType")
    return (
      (Array.isArray(local[filter.key]) && local[filter.key].length > 0) ||
      String(local[filter.searchKey] ?? "").trim() !== "" ||
      String(local[filter.readKey] ?? "") !== ""
    );
  if (filter.type === "dateRange")
    return Boolean(local[filter.fromKey] || local[filter.toKey]);
  return String(local[filter.key] ?? "").trim() !== "";
}

const chipFilters = computed(() => [
  ...BASE_FILTERS.filter(hasFilterValue),
  ...activeFilters.value.filter(hasFilterValue),
]);

// Filtros com controle próprio à vista (campos da linha principal e o botão
// "Minhas Notas"): sozinhos, não abrem o painel.
const PRIMARY_KEYS = ["noteTitle", "noteBody", "activityTitle", "onlyMine"];
const panelOnlyChips = computed(() =>
  chipFilters.value.filter((f) => !PRIMARY_KEYS.includes(f.key))
);

const isPanelVisible = computed(
  () => isAdvancedPanelOpen.value || panelOnlyChips.value.length > 0
);

// Descarta as opções abertas no dropdown que ficaram sem preenchimento.
function pruneEmptyFilters() {
  activeFilterKeys.value = activeFilterKeys.value.filter((key) =>
    hasFilterValue(ADVANCED_FILTERS.find((f) => f.key === key))
  );
  addedInThisSession.value = [];
}

function closeAddFilterDropdown() {
  isAddFilterOpen.value = false;
  pruneEmptyFilters();
}

function toggleAdvancedPanel() {
  isAdvancedPanelOpen.value = !isAdvancedPanelOpen.value;
  if (!isAdvancedPanelOpen.value) closeAddFilterDropdown();
}

// O menu pode abrir mesmo sem filtros restantes: opções com chip reabrem em edição.
const hasAddFilterContent = computed(
  () => remainingFilters.value.length > 0 || panelOnlyChips.value.length > 0
);

// Abre o dropdown com uma opção em modo de edição. Sem `focusKey`, cai na
// primeira opção disponível (comportamento do botão "Adicionar filtro").
function openAddFilterDropdown(focusKey = null) {
  if (!hasAddFilterContent.value) return;
  isAddFilterOpen.value = true;
  // Opções que já têm chip entram listadas e expandidas, exibindo seus valores.
  addedInThisSession.value = activeFilterKeys.value.filter((key) =>
    hasFilterValue(ADVANCED_FILTERS.find((f) => f.key === key))
  );
  const target =
    focusKey && addFilterOptions.value.some((f) => f.key === focusKey)
      ? focusKey
      : addFilterOptions.value[0]?.key;
  if (target) selectFilterOption(target);
}

function toggleAddFilterDropdown() {
  if (isAddFilterOpen.value) {
    closeAddFilterDropdown();
    return;
  }
  openAddFilterDropdown();
}

// Seleção (via setas ou abertura do menu) já coloca o campo em modo de edição:
// expande a opção e foca o input, sem precisar de Enter. Checkbox não expande —
// marcar/desmarcar fica no Espaço para não ativar o filtro só de passagem.
function selectFilterOption(key) {
  const def = ADVANCED_FILTERS.find((f) => f.key === key);
  if (!def) return;
  if (def.type !== "checkbox" && !activeFilterKeys.value.includes(key)) {
    activeFilterKeys.value.push(key);
    addedInThisSession.value.push(key);
  }
  nextTick(() => {
    const menu = addFilterMenuRef.value;
    if (!menu) return;
    const option = menu.querySelector(`[data-filter-key="${CSS.escape(key)}"]`);
    if (!option) return;
    const target =
      def.type === "checkbox"
        ? option.querySelector(".add-filter-name")
        : option.querySelector('input:not([type="checkbox"])') ||
          option.querySelector(".add-filter-name");
    target?.focus();
  });
}

// Ao navegar para fora, opção expandida que ficou vazia volta a recolher.
function collapseFilterIfEmpty(key) {
  const def = ADVANCED_FILTERS.find((f) => f.key === key);
  if (!def || def.type === "checkbox" || hasFilterValue(def)) return;
  activeFilterKeys.value = activeFilterKeys.value.filter((k) => k !== key);
  addedInThisSession.value = addedInThisSession.value.filter((k) => k !== key);
}

// Setas movem a seleção entre as opções: cima/baixo percorrem a coluna (com
// volta); esquerda/direita alternam entre as colunas mantendo a linha.
// Em campos já preenchidos, ←/→ continuam movendo o cursor do input.
function handleAddFilterMenuKeydown(event) {
  // Tab não circula entre as opções — a navegação é pelas setas.
  if (event.key === "Tab") {
    event.preventDefault();
    return;
  }
  // Enter fecha o menu; os valores preenchidos viram chips.
  if (event.key === "Enter") {
    event.preventDefault();
    closeAddFilterDropdown();
    return;
  }
  if (!["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(event.key))
    return;

  const optionEl = event.target.closest("[data-filter-key]");
  if (!optionEl) return;
  const currentKey = optionEl.dataset.filterKey;

  const horizontal = event.key === "ArrowLeft" || event.key === "ArrowRight";
  const isTextInput =
    event.target.matches("input") && event.target.type !== "checkbox";
  if (horizontal && isTextInput && event.target.value !== "") return;

  const columns = addFilterColumns.value;
  let col = columns.findIndex((c) => c.some((f) => f.key === currentKey));
  if (col === -1) return;
  let row = columns[col].findIndex((f) => f.key === currentKey);

  if (event.key === "ArrowDown") {
    row = (row + 1) % columns[col].length;
  } else if (event.key === "ArrowUp") {
    row = (row - 1 + columns[col].length) % columns[col].length;
  } else {
    const nextCol = (col + 1) % columns.length;
    if (!columns[nextCol]?.length) return;
    col = nextCol;
    row = Math.min(row, columns[col].length - 1);
  }

  event.preventDefault();
  collapseFilterIfEmpty(currentKey);
  selectFilterOption(columns[col][row].key);
}

function addFilter(key) {
  if (activeFilterKeys.value.includes(key)) return;
  activeFilterKeys.value.push(key);
  addedInThisSession.value.push(key);
  const def = ADVANCED_FILTERS.find((f) => f.key === key);
  // Checkbox adicionado já entra ativo — adicionar o filtro é a própria intenção.
  if (def?.type === "checkbox") local[key] = true;
  focusAddedFilterInput(key);
}

// Ao adicionar um filtro pelo menu "Adicionar filtro", foca automaticamente o
// respectivo campo (após o v-if renderizar o input) para o usuário já digitar.
// Checkbox não tem campo de texto, então é ignorado.
function focusAddedFilterInput(key) {
  nextTick(() => {
    const menu = addFilterMenuRef.value;
    if (!menu) return;
    const option = menu.querySelector(`[data-filter-key="${CSS.escape(key)}"]`);
    const input = option?.querySelector('input:not([type="checkbox"])');
    if (input) input.focus();
  });
}

// Clique na opção: abre o campo; se já estiver aberto e sem preenchimento, recolhe.
function toggleFilterOption(key) {
  if (!activeFilterKeys.value.includes(key)) {
    addFilter(key);
    return;
  }
  const def = ADVANCED_FILTERS.find((f) => f.key === key);
  if (!hasFilterValue(def)) removeFilter(key);
}

// Clique em qualquer ponto da linha de um filtro checkbox alterna o valor
// (marca/desmarca), como se fosse o próprio checkbox. Mantém a opção "adicionada"
// para o checkbox permanecer visível; desmarcada, é descartada ao fechar o menu
// (pruneEmptyFilters). O clique no checkbox usa @click.stop, evitando duplo-toggle.
function toggleCheckboxFilter(key) {
  if (!activeFilterKeys.value.includes(key)) {
    activeFilterKeys.value.push(key);
    addedInThisSession.value.push(key);
  }
  local[key] = !local[key];
}

function removeFilter(key) {
  const def = ALL_FILTERS.find((f) => f.key === key);
  if (def?.type === "dateRange") {
    local[def.fromKey] = "";
    local[def.toKey] = "";
  } else if (def?.type === "checkbox") {
    local[key] = false;
  } else if (def?.type === "observationType") {
    local[key] = [];
    if (def.searchKey) local[def.searchKey] = "";
    if (def.readKey) local[def.readKey] = "";
  } else if (def) {
    local[key] = "";
  }
  activeFilterKeys.value = activeFilterKeys.value.filter((k) => k !== key);
  addedInThisSession.value = addedInThisSession.value.filter((k) => k !== key);
}

// Clique na chip → editar o filtro correspondente. Filtros avançados reabrem no
// dropdown "Adicionar filtro" já em modo de edição; filtros da linha principal
// (base) apenas focam o próprio campo/botão, sem duplicar dentro do dropdown.
function editFilter(key) {
  if (PRIMARY_KEYS.includes(key)) {
    closeAddFilterDropdown();
    focusBaseFilter(key);
    return;
  }
  openAddFilterDropdown(key);
}

function focusBaseFilter(key) {
  const refs = {
    noteTitle: noteTitleInputRef,
    noteBody: noteBodyInputRef,
    activityTitle: activityTitleInputRef,
    onlyMine: myNotesButtonRef,
  };
  nextTick(() => {
    const el = refs[key]?.value;
    if (!el) return;
    el.focus();
    if (typeof el.select === "function") el.select();
  });
}

function formatChipDate(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  const [year, month, day] = raw.split("-");
  return day && month && year ? `${day}/${month}/${year.slice(-2)}` : raw;
}

// Rótulo da chip — curto e semântico. Os campos de preenchimento vivem no dropdown.
function chipLabel(filter) {
  if (filter.type === "checkbox") return filter.short;

  if (filter.type === "observationType") {
    const sel = (local[filter.key] || [])
      .map((t) => OBSERVATION_TYPE_OPTIONS.find((o) => o.value === t)?.label || t)
      .join(", ");
    const term = String(local[filter.searchKey] ?? "").trim();
    const read = OBSERVATION_READ_OPTIONS.find(
      (o) => o.value === local[filter.readKey]
    )?.label;
    const parts = [];
    if (sel) parts.push(sel);
    if (read) parts.push(read);
    if (term) parts.push(`"${term}"`);
    return parts.length ? `${filter.short}: ${parts.join(" · ")}` : `${filter.short}…`;
  }

  if (filter.type === "dateRange") {
    const from = formatChipDate(local[filter.fromKey]);
    const to = formatChipDate(local[filter.toKey]);
    if (from && to) return `${filter.short}: ${from} → ${to}`;
    if (from) return `${filter.short} ≥ ${from}`;
    if (to) return `${filter.short} ≤ ${to}`;
    return `${filter.short}…`;
  }

  const value = String(local[filter.key] ?? "").trim();
  if (!value) return `${filter.short}…`;
  // "quoted": o próprio rótulo já diz a relação (ex.: Compartilhada com grupo "Crianças")
  if (filter.verb === "quoted") return `${filter.short} "${value}"`;
  return filter.verb === "="
    ? `${filter.short} = ${value}`
    : `${filter.short} contém "${value}"`;
}


// ─────────────────────────────────────────────────────────────────────────────
// BOTÃO "MINHAS NOTAS"
// ─────────────────────────────────────────────────────────────────────────────


// ✅ PATCH: isMyNotesActive agora reflete o flag onlyMine da store,
// em vez de comparar creatorName com o nome do usuário logado.
const isMyNotesActive = computed(() => store.filters.onlyMine === true);

// ✅ PATCH: toggleMyNotes agora seta onlyMine em vez de creatorName,
// garantindo que o backend filtre por CreatedByUserId (exato) e não
// por busca textual parcial no nome do criador.
function toggleMyNotes() {
  // O watcher de store.filters já volta à página 1 e refaz a busca; resetar
  // currentPage aqui somava um segundo fetch idêntico.
  local.onlyMine = !isMyNotesActive.value;
}

// ─────────────────────────────────────────────────────────────────────────────
// ACL / SELEÇÃO
// ─────────────────────────────────────────────────────────────────────────────
function hasPermission(note, ...names) {
  const perms = note?.userPermissions ?? [];
  return names.some((name) =>
    perms.some(
      (p) => String(p.permissionName ?? "").toLowerCase() === name.toLowerCase()
    )
  );
}

function isNoteCreator(note) {
  return (
    Number(userStore.currentUser?.userId) === Number(note?.createdByUserId)
  );
}
function isTextCreator(note) {
  return (
    Number(userStore.currentUser?.userId) === Number(note?.textCreatedByUserId)
  );
}
function isActivityCreator(note) {
  return (
    Number(userStore.currentUser?.userId) ===
    Number(note?.activityCreatedByUserId)
  );
}

function canViewPermissions(note) {
  return isNoteCreator(note) || isTextCreator(note) || isActivityCreator(note);
}

// ── NOVO: guards combinam vínculo (portão) AND grupo (filtro interno) ─────────
function canEditNote(note) {
  return (
    memberCanEditNote.value &&
    (isNoteCreator(note) || isSys.value || hasPermission(note, "editar"))
  );
}
function canDeleteNote(note) {
  return (
    memberCanDeleteNote.value &&
    (canEditNote(note) || isActivityCreator(note) || isTextCreator(note))
  );
}

function canViewObservation(note) {
  return (
    !!String(note?.observation ?? "").trim() &&
    (isNoteCreator(note) || isTextCreator(note) || isActivityCreator(note))
  );
}
function canEditObservation(note) {
  return (
    (isTextCreator(note) || isActivityCreator(note)) && !isNoteCreator(note)
  );
}
function canSelect(note) {
  return canDeleteNote(note);
}

const anySelectableOnPage = computed(() =>
  store.filteredNotes.some((n) => canSelect(n))
);

const isAllSelectedOnPage = computed({
  get: () => {
    const selectableIds = store.filteredNotes
      .filter((n) => canSelect(n))
      .map((n) => n.noteId);
    if (selectableIds.length === 0) return false;
    return selectableIds.every((id) => store.selectedNoteIds.includes(id));
  },
  set: (value) => {
    const selectableIds = store.filteredNotes
      .filter((n) => canSelect(n))
      .map((n) => n.noteId);
    if (value) {
      selectableIds.forEach((id) => {
        if (!store.selectedNoteIds.includes(id)) store.selectedNoteIds.push(id);
      });
    } else {
      store.selectedNoteIds = store.selectedNoteIds.filter(
        (id) => !selectableIds.includes(id)
      );
    }
  },
});

// ─────────────────────────────────────────────────────────────────────────────
// FETCH
// ─────────────────────────────────────────────────────────────────────────────
async function fetchNotes() {
  const requestId = ++fetchNotesRequestId.value;

  try {
    // ✅ PATCH: enquanto busca, não exibe o empty-state de "nenhuma nota"
    isLoadingNotes.value = true;

    await store.fetchNotesGeneralPaged(currentPage.value, pageSize.value);
  } catch (error) {
    console.error("❌ Erro ao buscar notas:", error?.message);
    showErrorToast("Erro ao carregar notas.");
  } finally {
    // ✅ PATCH: só a requisição mais recente pode encerrar o loading
    if (requestId === fetchNotesRequestId.value) {
      isLoadingNotes.value = false;
    }
  }
}
watch(currentPage, fetchNotes);

// ─────────────────────────────────────────────────────────────────────────────
// CLICK FORA
// ─────────────────────────────────────────────────────────────────────────────
const openDropdownNoteId = ref(null);

function toggleNoteDropdown(noteId) {
  openDropdownNoteId.value =
    openDropdownNoteId.value === noteId ? null : noteId;
}

function handleClickOutside(event) {
  if (
    openDropdownNoteId.value &&
    !event.target.closest(".note-card-dropdown-anchor")
  )
    openDropdownNoteId.value = null;
  if (
    isBatchActionsOpen.value &&
    !event.target.closest(".batch-actions-dropdown-anchor")
  )
    isBatchActionsOpen.value = false;
  if (
    isAddFilterOpen.value &&
    !event.target.closest(".add-filter-dropdown-anchor")
  )
    closeAddFilterDropdown();

  // Foco saiu do botão "Mais filtros" e do painel sem nenhum filtro
  // efetivamente marcado → o botão volta ao estado desmarcado.
  if (
    isAdvancedPanelOpen.value &&
    !event.target.closest(".advanced-filters-anchor") &&
    !event.target.closest(".active-filters-panel") &&
    panelOnlyChips.value.length === 0
  ) {
    isAdvancedPanelOpen.value = false;
    closeAddFilterDropdown();
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS VISUAIS
// ─────────────────────────────────────────────────────────────────────────────
function getCreatorAvatarUrl(note) {
  const path = note.createdByUserPhotoPath || "";
  if (!path) return null;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  const base = (api.defaults.baseURL || "")
    .replace(/\/api$/, "")
    .replace(/\/+$/, "");
  return base + (path.startsWith("/") ? path : `/${path}`);
}

// ── Critério de estilização de tags espelhado de NoteList.vue ────────────────
function isSingleWord(text) {
  return (text ?? "").trim().split(/\s+/).length === 1;
}

function noteIsSingleWord(note) {
  // noteLength é o comprimento exato do trecho marcado no texto
  // Se não disponível, calcula a partir dos ranges
  const len =
    note.noteLength ??
    ((note.textRangeEnd ?? 0) - (note.textRangeStart ?? 0));
  if (len > 0) {
    // Trecho com espaço certamente é multipalavra
    // Para ter certeza, verifica o título (preenchido com o trecho selecionado)
    return isSingleWord(note.title ?? "");
  }
  return isSingleWord(note.title ?? "");
}

function getTagStyle(note, index) {
  const textColor = note.tagSpecificColors?.[index] || "#111827";
  const bgColor =
    note.tagSpecificBackgrounds?.[index] ?? "var(--bg-active-nav)";

  if (noteIsSingleWord(note)) {
    // Nota de palavra única: tag em negrito com cor da tag, sem background
    return {
      color: textColor,
      fontWeight: "600",
    };
  } else {
    // Nota de trecho multipalavra: tag apenas com background, texto herda cor e peso do body
    return {
      backgroundColor: bgColor,
    };
  }
}

function formatNoteDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  return (
    d.toLocaleDateString("pt-BR") +
    " às " +
    d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
  );
}

function hasZeroRange(note) {
  const start = note.textRangeStart ?? note.textRangeStar ?? null;
  const end = note.textRangeEnd ?? null;
  return start === 0 && end === 0;
}

function escapeHtml(str) {
  return String(str ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
function escapeRegex(str) {
  return String(str ?? "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

const highlightTerms = computed(() => {
  return [local.noteBody, local.source]
    .map((t) => String(t ?? "").trim())
    .filter(Boolean)
    .filter(
      (term, index, arr) =>
        arr.findIndex(
          (x) => normalizeForSearch(x) === normalizeForSearch(term)
        ) === index
    )
    .sort((a, b) => b.length - a.length);
});

/**
 * Versão normalizada do texto (sem acento/caixa) acompanhada do índice, no texto
 * original, que deu origem a cada posição. O mapa é necessário porque normalizar
 * muda o comprimento da string ("í" vira "i" + diacrítico combinante): sem ele
 * não dá para voltar do trecho casado para o trecho original a ser destacado.
 */
function foldWithIndexMap(str) {
  let folded = "";
  const indexMap = [];
  for (let i = 0; i < str.length; i++) {
    const piece = normalizeForSearch(str[i]);
    for (let k = 0; k < piece.length; k++) indexMap.push(i);
    folded += piece;
  }
  indexMap.push(str.length); // sentinela: fecha um match que vai até o fim
  return { folded, indexMap };
}

// Realce dos termos pesquisados. A busca roda sobre o texto normalizado — o
// servidor usa collation utf8mb4_0900_ai_ci, então "capitulo" traz "Capítulo" e
// o destaque precisa acompanhar. O casamento é feito no texto cru e o escape de
// HTML aplicado por segmento: procurar no texto já escapado marcaria trechos
// dentro das entidades (buscar "amp" acertaria o meio de "&amp;").
function highlightSearchableText(value) {
  const raw = String(value ?? "");
  if (!highlightTerms.value.length || !raw) return escapeHtml(raw);

  const terms = highlightTerms.value.map(normalizeForSearch).filter(Boolean);
  if (!terms.length) return escapeHtml(raw);

  const { folded, indexMap } = foldWithIndexMap(raw);
  const regex = new RegExp(terms.map(escapeRegex).join("|"), "g");

  let out = "";
  let cursor = 0;
  let match;
  while ((match = regex.exec(folded)) !== null) {
    const start = indexMap[match.index];
    const end = indexMap[match.index + match[0].length];
    if (start < cursor) continue; // termos sobrepostos: mantém o primeiro
    out += escapeHtml(raw.slice(cursor, start));
    out += `<mark class="search-highlight">${escapeHtml(
      raw.slice(start, end)
    )}</mark>`;
    cursor = end;
  }
  out += escapeHtml(raw.slice(cursor));
  return out;
}


// ─────────────────────────────────────────────────────────────────────────────
// ANEXOS E PREVIEWS DE LINKS (src/utils/noteLinks.js, compartilhado com NoteList)
// A verificação dos links roda em paralelo, fora do carregamento das notas: os
// cards aparecem na hora e só ganham o selo de "quebrado" quando o veredito chega.
// ─────────────────────────────────────────────────────────────────────────────
const { linkStatus, ensureLinkPreviews } = useLinkPreviews();

watch(
  () => store.filteredNotes,
  (notes) => {
    const urls = (notes ?? []).flatMap((note) => [
      ...extractUrls(note.body),
      ...extractUrls(note.source),
      ...extractUrls(note.observation),
    ]);
    if (urls.length) ensureLinkPreviews(urls);
  },
  { immediate: true }
);

const showAttachmentModal = ref(false);
const attachmentList = ref([]);

function openAttachmentModal(note) {
  const list = getNoteAttachments(note);
  if (!list.length) return;
  attachmentList.value = list;
  showAttachmentModal.value = true;
}

function closeAttachmentModal() {
  showAttachmentModal.value = false;
  attachmentList.value = [];
}

function buildBodyPreview(note) {
  // Colapsa 3+ quebras de linha consecutivas (mesmo com espaços entre elas)
  // em duas — várias linhas em branco viram uma só. 1 ou 2 quebras são mantidas.
  const raw = String(note.body ?? "")
    .replace(/[ \t]*\n(?:[ \t]*\n){2,}[ \t]*/g, "\n\n")
    .trim();
  if (!raw) return "—";
  // renderText: o texto entre os links passa pelo realce dos termos pesquisados.
  return linkifyBody(raw, {
    renderText: highlightSearchableText,
    getStatus: (url) => linkStatus[url],
  });
}
function buildSourceHtml(value) {
  return highlightSearchableText(value);
}
function hasTags(note) {
  return Array.isArray(note.tagNames) && note.tagNames.length > 0;
}
function hasSource(note) {
  return String(note.source ?? "").trim() !== "";
}
function hasFooterContent(note) {
  return Boolean(note.textTitle || note.activityTitle || hasSource(note));
}

// ─────────────────────────────────────────────────────────────────────────────
// LIFECYCLE
// ─────────────────────────────────────────────────────────────────────────────
onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  fetchNotes();
  // A tela abre pronta para filtrar, com o cursor no primeiro campo.
  focusBaseFilter("noteTitle");
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  Object.keys(local).forEach((k) => {
    if (typeof local[k] === "boolean") local[k] = false;
    else if (Array.isArray(local[k])) local[k] = [];
    else local[k] = "";
  });
  store.clearFilters();
  store.selectedNoteIds = [];
  noteStore.selectedNoteIds = [];
});
</script>

<template>
  <div class="w-full h-full">
    <div class="main-container">
      <div class="main-header">
        <div class="flex flex-col gap-0">
          <div class="text-xl font-bold">Pesquisa por Notas</div>
          <div class="text-sm mt-1 text-gray-700">
            <template v-if="isSys"
              >Explore e gerencie notas de todos os usuários do
              sistema.</template
            >
            <template v-else
              >Explore suas notas, trechos destacados e conexões entre
              textos.</template
            >
          </div>
        </div>
      </div>

      <div class="action-bar action-bar-stacked">
        <div class="filter-panel w-full">
          <div class="filter-row">
            <div class="filter-field filter-field-wide">
              <label class="filter-label">Título da nota</label>
              <input
                ref="noteTitleInputRef"
                v-model="local.noteTitle"
                type="text"
                placeholder="ex: Análise do herói…"
                class="filter-input"
              />
            </div>
            <div class="filter-field filter-field-wide">
              <label class="filter-label">Conteúdo da nota</label>
              <input
                ref="noteBodyInputRef"
                v-model="local.noteBody"
                type="text"
                placeholder="ex: protagonista…"
                class="filter-input"
              />
            </div>
            <div class="filter-field filter-field-wide">
              <label class="filter-label">Título da atividade</label>
              <input
                ref="activityTitleInputRef"
                v-model="local.activityTitle"
                type="text"
                placeholder="ex: Capítulo 1…"
                class="filter-input"
              />
            </div>

            <div class="filter-actions-right">
              <div
                class="filter-field filter-field-batch-actions advanced-filters-anchor"
              >
                <label class="filter-label">&nbsp;</label>
                <button
                  type="button"
                  class="btn btn-batch btn-advanced"
                  :class="{ active: isPanelVisible }"
                  title="Exibir mais filtros"
                  @click.stop="toggleAdvancedPanel"
                >
                  <i class="fas fa-filter"></i>
                  <span>Mais filtros</span>
                  <span v-if="chipFilters.length" class="advanced-count">{{
                    chipFilters.length
                  }}</span>
                  <svg
                    class="-mr-1 ml-1.5 w-5 h-5 batch-chevron"
                    :class="{ open: isAdvancedPanelOpen }"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    aria-hidden="true"
                  >
                    <path
                      clip-rule="evenodd"
                      fill-rule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    />
                  </svg>
                </button>
              </div>

              <div
                v-if="hasActiveFilters"
                class="filter-field filter-field-clear"
              >
                <label class="filter-label">&nbsp;</label>
                <button
                  class="filter-clear"
                  title="Limpar filtros"
                  @click="clearAllFilters"
                >
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    class="clear-icon"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  <span>Limpar</span>
                </button>
              </div>

            </div>
          </div>

          <!-- Painel de filtros ativos -->
          <div v-if="isPanelVisible" class="active-filters-panel">
            <div class="active-filters-title">
              <i class="fas fa-filter"></i>
              <span>Filtros ativos</span>
            </div>

            <div v-if="chipFilters.length" class="active-filters-chips">
              <div
                v-for="filter in chipFilters"
                :key="filter.key"
                class="filter-chip filter-chip--clickable"
                :title="chipLabel(filter)"
                role="button"
                tabindex="0"
                @click.stop="editFilter(filter.key)"
                @keydown.enter.prevent="editFilter(filter.key)"
                @keydown.space.prevent="editFilter(filter.key)"
              >
                <i :class="filter.icon" class="filter-chip-icon"></i>
                <span class="filter-chip-label">{{ chipLabel(filter) }}</span>
                <button
                  type="button"
                  class="filter-chip-remove"
                  :title="`Remover filtro ${filter.label}`"
                  @click.stop="removeFilter(filter.key)"
                >
                  <i class="fas fa-times"></i>
                </button>
              </div>
            </div>

            <p v-else class="active-filters-empty">
              Nenhum filtro adicionado.
            </p>

            <div
              class="add-filter-dropdown-anchor"
              style="position: relative; align-self: flex-start"
            >
              <button
                type="button"
                class="btn btn-batch btn-add-filter"
                :disabled="!hasAddFilterContent"
                @click.stop="toggleAddFilterDropdown"
              >
                <i class="fas fa-plus"></i>
                <span>Adicionar filtro</span>
                <svg
                  class="-mr-1 ml-1.5 w-5 h-5 batch-chevron"
                  :class="{ open: isAddFilterOpen }"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path
                    clip-rule="evenodd"
                    fill-rule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  />
                </svg>
              </button>

              <div
                v-if="isAddFilterOpen && addFilterOptions.length > 0"
                ref="addFilterMenuRef"
                class="dropdown-menu add-filter-menu"
                @keydown="handleAddFilterMenuKeydown"
              >
                <div class="add-filter-grid">
                  <div
                    v-for="(column, columnIndex) in addFilterColumns"
                    :key="`add-filter-col-${columnIndex}`"
                    class="add-filter-column"
                  >
                    <div
                      v-for="filter in column"
                      :key="filter.key"
                      :data-filter-key="filter.key"
                      class="add-filter-option"
                      :class="{
 'is-open': isFilterAdded(filter.key),
                        'is-checkbox': filter.type === 'checkbox',
                      }"
                      @click.stop="
                        filter.type === 'checkbox'
                          ? toggleCheckboxFilter(filter.key)
                          : toggleFilterOption(filter.key)
                      "
                    >
                      <button
                        type="button"
                        class="add-filter-name"
                        @click.stop="
                          filter.type === 'checkbox'
                            ? toggleCheckboxFilter(filter.key)
                            : toggleFilterOption(filter.key)
                        "
                      >
                        <i :class="filter.icon" class="add-filter-icon"></i>
                        <span>{{ filter.label }}</span>
                      </button>

                      <template v-if="isFilterAdded(filter.key)">
                        <template v-if="filter.type === 'dateRange'">
                          <div class="add-filter-inputs">
                            <span class="add-filter-sublabel">de</span>
                            <input
                              v-model="local[filter.fromKey]"
                              type="date"
                              class="add-filter-input add-filter-input-date"
                              @click.stop
                            />
                            <span class="add-filter-sublabel">até</span>
                            <input
                              v-model="local[filter.toKey]"
                              type="date"
                              class="add-filter-input add-filter-input-date"
                              @click.stop
                            />
                          </div>
                        </template>

                        <template v-else-if="filter.type === 'checkbox'">
                          <input
                            type="checkbox"
                            v-model="local[filter.key]"
                            class="checkbox-style"
                            @click.stop
                          />
                        </template>

                        <template v-else-if="filter.type === 'observationType'">
                          <input
                            v-model="local[filter.searchKey]"
                            type="text"
                            :placeholder="filter.placeholder"
                            class="add-filter-input"
                            @click.stop
                          />
                          <div class="add-filter-obs-types" @click.stop>
                            <label
                              v-for="opt in OBSERVATION_TYPE_OPTIONS"
                              :key="opt.value"
                              class="obs-type-item"
                              :style="{ '--type-color': opt.color }"
                            >
                              <input
                                type="checkbox"
                                :checked="(local[filter.key] || []).includes(opt.value)"
                                class="obs-type-checkbox"
                                @click.stop
                                @change="toggleObservationType(filter.key, opt.value)"
                              />
                              <span>{{ opt.label }}</span>
                            </label>

                            <!-- Leitura: mesmo grupo visual, encostado à direita
                                 e separado por um traço — é outra dimensão (AND). -->
                            <span class="obs-group-divider" aria-hidden="true"></span>

                            <label
                              v-for="opt in OBSERVATION_READ_OPTIONS"
                              :key="opt.value"
                              class="obs-type-item"
                              :style="{ '--type-color': opt.color }"
                            >
                              <input
                                type="checkbox"
                                :checked="local[filter.readKey] === opt.value"
                                class="obs-type-checkbox"
                                @click.stop
                                @change="toggleObservationReadState(filter.readKey, opt.value)"
                              />
                              <span>{{ opt.label }}</span>
                            </label>
                          </div>
                        </template>

                        <template v-else>
                          <input
                            v-model="local[filter.key]"
                            type="text"
                            :inputmode="
                              filter.type === 'numeric' ? 'numeric' : undefined
                            "
                            :placeholder="filter.placeholder"
                            class="add-filter-input"
                            @click.stop
                          />
                        </template>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="filter-secondary-row">
            <button
              ref="myNotesButtonRef"
              type="button"
              class="btn btn-batch btn-my-notes"
              :class="{ active: isMyNotesActive }"
              title="Filtrar apenas minhas notas"
              @click="toggleMyNotes"
            >
              <i class="fas fa-user"></i>
              <span>Minhas Notas</span>
            </button>

            <div
              class="batch-actions-dropdown-anchor"
              style="position: relative"
            >
              <button
                type="button"
                class="btn btn-batch"
                :disabled="store.selectedNoteIds.length === 0"
                @click.stop="toggleBatchActionsDropdown"
              >
                Ações em lote
                <svg
                  class="-mr-1 ml-1.5 w-5 h-5 batch-chevron"
                  :class="{ open: isBatchActionsOpen }"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path
                    clip-rule="evenodd"
                    fill-rule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  />
                </svg>
              </button>

              <div
                v-if="isBatchActionsOpen && store.selectedNoteIds.length > 0"
                class="dropdown-menu"
              >
                <div class="py-1">
                  <a
                    href="#"
                    role="button"
                    @click.prevent.stop="
                      memberCanDeleteNote && openBatchDeleteModal()
                    "
                    class="dropdown-item danger-item"
                    :class="{
 'dropdown-item-disabled': !memberCanDeleteNote,
                    }"
                    :title="
                      !memberCanDeleteNote
                        ? 'Sem permissão para excluir notas neste vínculo'
                        : undefined
                    "
                  >
                    <i class="fas fa-trash-alt"></i>
                    <span>Apagar selecionadas</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="controls-bar">
          <div class="controls-left">
            <label
              class="checkbox-all-label"
              title="Selecionar / Desselecionar todos da página"
              :class="{ 'checkbox-all-label-disabled': !anySelectableOnPage }"
            >
              <input
                type="checkbox"
                v-model="isAllSelectedOnPage"
                class="checkbox-style"
                :disabled="!anySelectableOnPage"
              />
              <span class="checkbox-all-text">Selecionar todas</span>
            </label>
            <div class="page-size-control">
              <label class="checkbox-all-text">Notas por página</label>
              <input
                type="number"
                v-model.number="pageSize"
                min="1"
                max="100"
                class="page-size-input"
              />
            </div>
            <span
              v-if="store.selectedNoteIds.length > 0"
              class="selection-badge"
            >
              {{ store.selectedNoteIds.length }}
              {{
                store.selectedNoteIds.length === 1
                  ? "nota selecionada"
                  : "notas selecionadas"
              }}
            </span>
          </div>
          <div class="controls-right">
            <template v-if="!isLoadingNotes && (store.totalCount ?? 0) > 0">
              <PaginationInfo
                :current-page="currentPage"
                :page-size="pageSize"
                :total-count="store.totalCount ?? 0"
              />
              <PaginationButtons
                :current-page="currentPage"
                :page-size="pageSize"
                :total-count="store.totalCount ?? 0"
                :total-pages="store.totalPages"
                @change-page="(page) => (currentPage = page)"
              />
            </template>
          </div>
        </div>

        <div
          class="notes-grid"
          v-if="!isLoadingNotes && store.filteredNotes.length > 0"
        >
          <div
            v-for="note in store.filteredNotes"
            :key="note.noteId"
            class="note-card"
            :class="{
 'note-observation-yellow':
                note.observation?.trim() &&
                note.observationType === 'Observation' &&
                !note.isObservationRead,
              'note-observation-green':
                note.observationType === 'Correct' && !note.isObservationRead,
              'note-observation-red':
                note.observationType === 'Incorrect' && !note.isObservationRead,
              'note-observation-blue':
                note.isObservationRead &&
                (note.observation?.trim() ||
                  note.observationType === 'Correct' ||
                  note.observationType === 'Incorrect'),
              'note-no-range': hasZeroRange(note),
              'dropdown-open': openDropdownNoteId === note.noteId,
            }"
          >
            <div class="card-header">
              <div
                class="author-info"
                @click.stop="openUserCardModal(note.createdByUserId)"
              >
                <div
                  v-if="getCreatorAvatarUrl(note)"
                  class="author-avatar"
                  :style="{
                    backgroundImage: `url('${getCreatorAvatarUrl(note)}')`,
                  }"
                ></div>
                <div v-else class="author-avatar author-avatar-fallback">
                  <i class="fas fa-user"></i>
                </div>
                <div class="author-meta">
                  <span class="author-name">{{
                    String(note.createdByUserName ?? "usuário").toLowerCase()
                  }}</span>
                  <span
                    v-if="formatNoteDate(note.updatedAt)"
                    class="note-date-header"
                    >{{ formatNoteDate(note.updatedAt) }}</span
                  >
                </div>
              </div>

              <div class="card-actions" @click.stop>
                <!-- Botão de anexo (clip): só quando a nota tem link de imagem,
                     documento ou site no corpo, na fonte ou na observação. -->
                <button
                  v-if="hasAttachment(note)"
                  type="button"
                  class="btn-icon-sm btn-icon-attachment--active tooltip-left-bottom"
                  data-tooltip="Ver anexo"
                  @click.stop="openAttachmentModal(note)"
                >
                  <i class="fas fa-paperclip"></i>
                </button>
                <button
                  v-if="note.activityId"
                  type="button"
                  class="btn-context tooltip-left-bottom"
                  data-tooltip="Abrir no contexto da atividade"
                  @click.stop="goToContext(note)"
                >
                  <i class="fas fa-file-signature"></i>
                </button>
                <button
                  type="button"
                  class="btn-icon-sm btn-icon-share tooltip-left-bottom"
                  data-tooltip="Compartilhar nota"
                  @click.stop="
                    openSharePopover(note.noteId, note.shareToken ?? null)
                  "
                >
                  <i class="fas fa-share-alt"></i>
                </button>
                <button
                  type="button"
                  class="btn-icon-sm btn-icon-brand tooltip-left-bottom"
                  data-tooltip="Ver nota completa"
                  @click.stop="openNoteCardModal(note.noteId)"
                >
                  <i class="fas fa-expand-alt"></i>
                </button>

                <div
                  class="note-card-dropdown-anchor"
                  style="position: relative"
                >
                  <button
                    type="button"
                    class="btn-icon-sm btn-icon-neutral tooltip-left-bottom"
                    data-tooltip="Opções"
                    @click.stop="toggleNoteDropdown(note.noteId)"
                  >
                    <i class="fas fa-ellipsis-h"></i>
                  </button>

                  <div
                    v-if="openDropdownNoteId === note.noteId"
                    class="card-dropdown-menu"
                  >
                    <ul>
                      <li
                        @click.stop="
                          openNoteCardModal(note.noteId);
                          openDropdownNoteId = null;
                        "
                      >
                        <i class="fas fa-eye"></i><span>Ver nota</span>
                      </li>
                      <li
                        v-if="canViewPermissions(note)"
                        @click.stop="openPermissionViewModal(note)"
                      >
                        <i class="fas fa-user-shield"></i
                        ><span>Ver permissões</span>
                      </li>
                      <li
                        v-if="canViewObservation(note)"
                        @click.stop="openReadObservationModal(note)"
                      >
                        <i class="fas fa-eye"></i><span>Ver Observação</span>
                      </li>
                      <li
                        v-if="note.activityId"
                        @click.stop="
                          goToContext(note);
                          openDropdownNoteId = null;
                        "
                      >
                        <i class="fas fa-file-signature"></i
                        ><span>Abrir no contexto</span>
                      </li>

                      <!-- Editar -->
                      <li
                        @click.stop="
                          canEditNote(note) && openEditNoteModal(note)
                        "
                        :class="{
 'dropdown-item-disabled': !canEditNote(note),
                        }"
                        :title="
                          !canEditNote(note)
                            ? 'Sem permissão para editar notas neste vínculo'
                            : undefined
                        "
                      >
                        <i class="fas fa-pencil-alt"></i><span>Editar</span>
                      </li>

                      <li
                        v-if="canEditObservation(note)"
                        @click.stop="openEditObservationModal(note)"
                      >
                        <i class="fas fa-comment-alt"></i>
                        <span>{{
                          note.observation
                            ? "Editar Observação"
                            : "Fazer Observação"
                        }}</span>
                      </li>

                      <!-- Apagar -->
                      <li
                        class="danger-item"
                        @click.stop="
                          canDeleteNote(note) && openDeleteNoteModal(note)
                        "
                        :class="{
 'dropdown-item-disabled': !canDeleteNote(note),
                        }"
                        :title="
                          !canDeleteNote(note)
                            ? 'Sem permissão para excluir notas neste vínculo'
                            : undefined
                        "
                      >
                        <i class="fas fa-trash-alt"></i><span>Apagar</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <input
                  v-if="canSelect(note)"
                  type="checkbox"
                  class="checkbox-style"
                  :checked="store.selectedNoteIds.includes(note.noteId)"
                  @change="store.toggleSelection(note.noteId)"
                  @click.stop
                />
                <div v-else style="width: 0.9rem"></div>
              </div>
            </div>

            <h4 class="note-title">{{ note.title }}</h4>

            <span
              v-if="hasZeroRange(note)"
              class="no-range-chip"
              title="Nota avulsa, sem vínculo com trechos do texto"
              >nota avulsa</span
            >

            <p class="note-body-preview" v-html="buildBodyPreview(note)"></p>

            <div v-if="hasTags(note)" class="note-tags-line">
              <i class="fas fa-hashtag tag-hash"></i>
              <button
                v-for="(tagName, index) in note.tagNames"
                :key="`tag-${note.tagIds?.[index] ?? index}`"
                type="button"
                class="subcategory-tag tag-link"
                :style="getTagStyle(note, index)"
                @click.stop="openTagCardModal(note, index)"
              >
                {{ tagName }}
              </button>
            </div>

            <div v-if="hasFooterContent(note)" class="card-footer">
              <div
                v-if="note.textTitle || note.activityTitle"
                class="footer-context-row"
              >
                <span
                  v-if="note.textTitle"
                  class="meta-chip meta-chip-text"
                  :title="
                    note.textSubtitle
                      ? `${note.textTitle}\n(${note.textSubtitle})`
                      : note.textTitle
                  "
                >
                  <span class="meta-chip-label">{{ note.textTitle }}</span>
                </span>
                <i
                  v-if="note.textTitle && note.activityTitle"
                  class="fas fa-chevron-right meta-sep"
                ></i>
                <span
                  v-if="note.activityTitle"
                  class="meta-chip meta-chip-activity"
                  :title="note.activityTitle"
                >
                  <span class="meta-chip-label">{{ note.activityTitle }}</span>
                </span>
              </div>
              <div v-if="hasSource(note)" class="footer-bottom-row">
                <span class="note-source" :title="note.source">
                  <i class="fas fa-link footer-icon"></i>
                  <span v-html="buildSourceHtml(note.source)"></span>
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- ✅ PATCH: estado de carregamento -->
        <div v-if="isLoadingNotes" class="empty-state loading-state">
          <svg class="empty-spinner" viewBox="0 0 24 24" fill="none">
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            />
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            />
          </svg>

          <p>Carregando notas...</p>
        </div>

        <!-- ✅ PATCH: estado vazio real, somente após terminar o carregamento -->
        <div v-else-if="store.filteredNotes.length === 0" class="empty-state">
          <img src="/img/lupa.webp" alt="" width="38" height="40" class="empty-icon-img" />
          <p>Nenhuma nota encontrada.</p>
        </div>

        <div
          v-if="!isLoadingNotes && (store.totalCount ?? 0) > 0"
          class="sub-pagination-row"
        >
          <div class="sub-pagination-left">
            <span class="sub-pagination-label">Mostrando</span>
            <PaginationInfo
              :current-page="currentPage"
              :page-size="pageSize"
              :total-count="store.totalCount ?? 0"
            />
            <span>registros</span>
          </div>

          <PaginationButtons
            :current-page="currentPage"
            :page-size="pageSize"
            :total-count="store.totalCount ?? 0"
            :total-pages="store.totalPages"
            @change-page="(page) => (currentPage = page)"
          />
        </div>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <NoteCardModal
      v-if="showNoteCardModal"
      :show="showNoteCardModal"
      :noteId="selectedNoteId"
      :userId="userStore.currentUser?.userId"
      @close="closeNoteCardModal"
      @refresh="handleNoteCardRefresh"
      @note-updated="handleNoteCardUpdated"
      @notify="handleNoteCardNotify"
    />
    <TagCardModal
      v-if="showTagCardModal"
      :show="showTagCardModal"
      :tagId="selectedTagId"
      @close="closeTagCardModal"
    />
    <UserCardModal
      v-if="showUserCardModal"
      :show="showUserCardModal"
      :userId="selectedUserId"
      @close="handleUserCardModalClose"
    />

    <!-- ── NOVO: EditNoteForm condicionado ao guard de edit ────────────────── -->
    <EditNoteForm
      v-if="showEditNoteModal && memberCanEditNote"
      :show="showEditNoteModal"
      :noteId="selectedNoteId"
      :initialStep="editInitialStep"
      @close="closeEditNoteModal"
    />

    <EditNoteObservationForm
      v-if="showEditObservationModal"
      :showObservationForm="showEditObservationModal"
      :noteId="selectedNoteId"
      @close="closeEditObservationModal"
    />
    <MarkObservationAsRead
      v-if="showReadObservationModal"
      :showReadConfirmation="showReadObservationModal"
      :noteId="selectedNoteId"
      :isViewingOnly="selectedNoteForObservation?.isObservationRead"
      @close="closeReadObservationModal"
    />

    <!-- ── NOVO: DeleteNoteForm condicionado ao guard de delete ───────────── -->
    <DeleteNoteForm
      v-if="showDeleteNoteModal && memberCanDeleteNote"
      :noteId="selectedNoteId"
      @close="closeDeleteNoteModal"
    />

    <PermissionView
      v-if="showPermissionViewModal"
      :show="showPermissionViewModal"
      entityType="note"
      :noteId="selectedNoteId"
      title="Permissões da nota"
      @close="closePermissionViewModal"
    />

    <!-- ── NOVO: BatchDeleteNoteForm condicionado ao guard de delete ──────── -->
    <BatchDeleteNoteForm
      v-if="showBatchDeleteModal && memberCanDeleteNote"
      :selectedNoteIds="noteStore.selectedNoteIds"
      @close="closeBatchDeleteModal"
    />

    <NoteAttachmentModal
      v-if="showAttachmentModal"
      :show="showAttachmentModal"
      :attachments="attachmentList"
      @close="closeAttachmentModal"
    />

    <ShareNotePopover
      v-if="isSharePopoverOpen"
      :show="isSharePopoverOpen"
      :noteTitle="sharingNoteTitle"
      :shareUrl="absoluteShareUrl"
      :isGenerating="isGenerating"
      :isCopied="isCopied"
      :isRevoking="isRevoking"
      :shareError="shareError"
      :noteId="shareNoteId"
      :isOwner="isShareOwner"
      @close="closeSharePopover"
      @generate="
        (id) => {
          console.log('[NoteList] generate recebido, id:', id);
          generateLink(id);
        }
      "
      @copy="copyLink"
      @share-whatsapp="shareWhatsApp"
      @share-email="shareEmail(sharingNoteTitle)"
      @share-native="shareNative(sharingNoteTitle)"
      @revoke="revokeLink"
    />
  </Teleport>

  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Erro" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>
          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            &times;
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
.main-header {
  flex-shrink: 0;
}

/* Contêiner da página inteira (filtros, barra de controles, cards): ocupa a
   altura restante e rola. O `.action-bar` canônico distribui os filhos com
   `space-between`, o que aqui empurrava o "Carregando notas..." para o rodapé. */
.action-bar {
  flex: 1;
  justify-content: flex-start;
  overflow-y: auto;
}
.action-bar-stacked {
  flex-direction: column !important;
  align-items: stretch !important;
}
.filter-panel {
  width: 100%;
  flex-shrink: 0;
}
.filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: flex-end;
}
.filter-field {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1 1 90px;
  min-width: 80px;
  max-width: 160px;
}
/* Os três campos dividem igualmente toda a largura livre até o botão de filtros */
.filter-field-wide {
  flex: 1 1 0;
  min-width: 130px;
  max-width: none;
}
.filter-field-clear {
  flex: 0 0 auto;
  min-width: unset;
  max-width: unset;
}
.filter-label {
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--text-light);
  white-space: nowrap;
  padding-left: 2px;
}
.filter-input {
  width: 100%;
  height: 32px;
  padding: 0.3rem 0.55rem;
  border: 1px solid var(--border-color);
  background-color: var(--surface);
  color: var(--text-dark);
  font-size: 0.8rem;
  border-radius: 8px;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;
}
.filter-input:focus {
  outline: none;
  border-color: var(--focus-border);
  box-shadow: var(--focus-ring);
}
.filter-clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  width: auto;
  height: 32px;
  padding: 0 0.65rem;
  flex-shrink: 0;
  border: 1px solid var(--danger);
  border-radius: 8px;
  background-color: var(--danger-bg);
  color: var(--danger-text);
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s, transform 0.15s;
  white-space: nowrap;
}
/* mesmo comportamento de clique do botão "Mais filtros" (.btn-batch) */
.filter-clear:hover {
  background-color: var(--danger-bg);
}
.filter-clear:active {
  transition: background-color 0.2s ease-in-out, transform 0.05s ease;
  transform: translateY(1px);
}
.clear-icon {
  width: 13px;
  height: 13px;
  flex-shrink: 0;
}
.controls-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  padding: 2px 0 4px;
  gap: 12px;
}
.controls-left {
  display: flex;
  align-items: center;
  gap: 10px;
}
.controls-right {
  display: flex;
  align-items: center;
  gap: 8px;
}
.checkbox-all-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  user-select: none;
  transition: opacity 0.15s ease;
}
.checkbox-all-text {
  font-size: 0.8rem;
  color: var(--text-light);
}
.selection-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  background-color: var(--brand-soft);
  color: var(--text-on-brand-soft);
}
.notes-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  width: 100%;
  box-sizing: border-box;
  flex-shrink: 0;
  align-items: start;
}
.note-card {
  background: var(--surface);
  border-radius: 18px;
  padding: 14px;
  border: 1px solid var(--border-color);
  border-left: 3px solid transparent;
  transition: box-shadow 0.22s ease, border-color 0.22s ease,
    transform 0.18s ease;
  display: flex;
  flex-direction: column;
  gap: 9px;
  position: relative;
  min-width: 0;
  box-sizing: border-box;
  width: 100%;
  box-shadow: 0 2px 8px rgba(17, 24, 39, 0.04);
}
.note-card:hover {
  box-shadow: 0 10px 28px rgba(17, 24, 39, 0.1);
  transform: translateY(-2px);
}
.note-observation-blue {
  background-color: var(--info-bg);
  border-left-color: var(--info);
}
.note-observation-yellow {
  background-color: var(--warning-bg);
  border-left-color: var(--warning);
}
.note-observation-green {
  background-color: var(--success-bg);
  border-left-color: var(--success);
}
.note-observation-red {
  background-color: var(--danger-bg);
  border-left-color: var(--danger);
}
.note-no-range {
  border-top: 2px dashed var(--brand);
}
.card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}
.author-info {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;
  flex: 1;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 8px;
  transition: background-color 0.2s, transform 0.05s ease;
}
.author-info:hover {
  background-color: var(--surface-hover);
}
.author-info:active {
  transform: translateY(1px);
}
.author-avatar {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background-color: var(--border-color);
  background-size: cover;
  background-position: center;
  flex-shrink: 0;
}
.author-avatar-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--border-color);
  color: var(--text-muted);
  font-size: 10px;
}
.author-meta {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.15;
  gap: 2px;
}
.author-name {
  font-size: 0.78rem;
  font-weight: 650;
  color: var(--text-dark);
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}
.note-date-header {
  font-size: 0.68rem;
  color: var(--text-light);
  font-style: italic;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}
.card-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}
.btn-context {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 26px;
  padding: 0 8px;
  border: none;
  border-radius: 8px;
  background-color: var(--focus-border);
  color: #1c3d5a;
  font-size: 0.72rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 0.2s ease-in-out, transform 0.05s ease;
}
.btn-context:hover {
  background-color: #82b4ff;
}
.btn-context:active {
  transform: translateY(1px);
}
.btn-context:focus,
.btn-context:focus-visible {
  outline: none;
  box-shadow: none;
}
.btn-icon-sm {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 9px;
  cursor: pointer;
  font-size: 0.78rem;
  transition: background-color 0.2s ease-in-out, transform 0.05s ease;
}
.btn-icon-sm:active:not(:disabled) {
  transform: translateY(1px);
}
.btn-icon-sm:focus,
.btn-icon-sm:focus-visible {
  outline: none;
  box-shadow: none;
}
.btn-icon-brand {
  background-color: var(--brand-soft);
  color: var(--text-on-brand-soft);
}
.btn-icon-brand:hover {
  background-color: var(--brand-soft-hover);
}
.btn-icon-neutral {
  background-color: var(--neutral-100);
  color: var(--text-dark);
}
.btn-icon-neutral:hover {
  background-color: var(--border-color);
}
.card-dropdown-menu {
  position: absolute;
  right: 0;
  top: calc(100% + 4px);
  z-index: 60;
  min-width: 160px;
  width: max-content;
  background-color: var(--surface);
  border-radius: 10px;
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-dropdown);
  overflow: hidden;
}
/* Eleva o card com dropdown aberto acima dos cards vizinhos */
.note-card.dropdown-open {
  z-index: 50;
}
.card-dropdown-menu ul {
  list-style: none;
  padding: 0;
  margin: 0;
}
.card-dropdown-menu li {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0.45rem 0.7rem;
  font-size: 0.8rem;
  line-height: 1.1;
  color: var(--text-dark);
  cursor: pointer;
  transition: background-color 0.2s ease-in-out, transform 0.05s ease;
  white-space: nowrap;
}
.card-dropdown-menu li:hover {
  background-color: var(--surface-hover);
}
.card-dropdown-menu li:active {
  transform: translateY(1px);
}
.card-dropdown-menu li:focus,
.card-dropdown-menu li:focus-visible {
  outline: none;
  box-shadow: none;
}
.card-dropdown-menu li.dropdown-item-disabled:hover {
  transform: none;
  box-shadow: none;
}
.card-dropdown-menu li i {
  width: 14px;
  text-align: center;
  font-size: 0.85em;
}
.card-dropdown-menu li.danger-item {
  color: var(--text-important);
}
.card-dropdown-menu li.danger-item:hover {
  background-color: var(--danger-hover-bg);
}
.note-title {
  font-size: 0.98rem;
  font-weight: 760;
  margin: 0;
  line-height: 1.35;
  color: var(--text-dark);
  overflow-wrap: anywhere;
  word-break: break-word;
  white-space: normal;
  letter-spacing: -0.01em;
}
.no-range-chip {
  align-self: flex-start;
  margin-top: -1px;
  padding: 2px 7px;
  border-radius: 9999px;
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  background-color: var(--brand-soft);
  color: var(--text-on-brand-soft);
}
.note-body-preview {
  font-size: 0.83rem;
  line-height: 1.58;
  color: var(--text-light);
  margin-top: -9px;
  overflow-wrap: anywhere;
  word-break: break-word;
  /* preserva as quebras de linha do corpo da nota */
  white-space: pre-wrap;
  overflow: visible;
  display: block;
}

/* ── Previews de links (imagem / documento / site) — espelhado de NoteList.vue ── */
.note-body-preview :deep(.note-media-row) {
  /* inline-flex (não flex/block) para não gerar linha em branco extra somada
     ao \n preservado pelo white-space: pre-wrap — mantém o lado-a-lado + wrap. */
  display: inline-flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 10px;
  vertical-align: top;
  margin: 0;
}
.note-body-preview :deep(.note-media-row .note-preview) {
  margin: 0;
}
.note-body-preview :deep(.note-preview) {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 96px;
  vertical-align: top;
  margin: 2px 6px 2px 0;
  text-decoration: none;
  line-height: 1.2;
}
.note-body-preview :deep(.note-preview-thumb) {
  width: 96px;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: 1px solid var(--border-color);
  background-color: var(--surface-2);
  box-shadow: 0 1px 3px rgba(17, 24, 39, 0.1);
  overflow: hidden;
  transition: transform 0.12s ease, box-shadow 0.15s ease;
}
.note-body-preview :deep(.note-preview:hover .note-preview-thumb) {
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(17, 24, 39, 0.16);
}
.note-body-preview :deep(.note-preview-img) {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.note-body-preview :deep(.note-preview-icon) {
  font-size: 2.1rem;
  line-height: 1;
}
.note-body-preview :deep(.note-preview-favicon) {
  width: 40px;
  height: 40px;
  object-fit: contain;
}
/* Fallback (imagem/favicon que não carrega): ícone genérico centralizado */
.note-body-preview :deep(.note-preview-fallback) {
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: var(--text-subtle);
}
.note-body-preview :deep(.note-preview-caption) {
  max-width: 96px;
  font-size: 0.72rem;
  color: var(--text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Selo do YouTube sobre a miniatura do vídeo */
.note-body-preview :deep(.note-preview-thumb) {
  position: relative;
}
.note-body-preview :deep(.note-preview-play) {
  position: absolute;
  bottom: 4px;
  right: 5px;
  font-size: 1.05rem;
  line-height: 1;
  color: #ff0000;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.45);
}

/* Link quebrado: moldura vermelha, ícone do tipo e selo de alerta */
.note-body-preview :deep(.note-preview-thumb--broken),
.note-body-preview :deep(.note-preview-fallback--broken) {
  position: relative;
  border-color: var(--danger-bg);
  background-color: var(--danger-bg);
  color: var(--danger-hover);
}
.note-body-preview :deep(.note-preview-thumb--broken .note-preview-icon) {
  color: var(--danger-hover);
  opacity: 0.55;
}
.note-body-preview :deep(.note-preview-badge) {
  position: absolute;
  bottom: 4px;
  right: 4px;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: var(--danger-hover);
  color: #fff;
  font-size: 0.55rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}
.note-body-preview :deep(.note-preview-caption--broken),
.note-body-preview :deep(.note-preview--broken .note-preview-caption) {
  color: var(--danger-text);
  text-decoration: line-through;
}
/* vídeo quebrado: sai o selo do YouTube (a moldura já está em vermelho) */
.note-body-preview :deep(.note-preview--broken .note-preview-play) {
  display: none;
}

/* Botão de anexo (clip) — sempre iluminado de amarelo, como em NoteList.vue */
.btn-icon-attachment--active {
  background-color: var(--warning-bg);
  color: var(--warning);
  box-shadow: 0 0 0 1px rgba(245, 158, 11, 0.35);
}
.btn-icon-attachment--active:hover {
  background-color: var(--warning-bg);
  color: var(--warning);
}
:deep(.search-highlight) {
  background-color: #fef08a;
  color: #713f12;
  border-radius: 3px;
  padding: 0 2px;
  font-weight: 700;
  box-shadow: inset 0 -1px 0 rgba(113, 63, 18, 0.15);
}
.note-tags-line {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  flex-wrap: wrap;
  font-size: 0.78rem;
  color: var(--text-light);
  min-height: 20px;
}
.tag-hash {
  font-size: 0.7rem;
  opacity: 0.8;
  margin-top: 6px;
}
/* ── Tags idênticas ao critério/formatação de NoteList.vue (.subcategory-tag) ──
   IMPORTANTE: usamos o mesmo nome de classe de NoteList.vue (em vez de
   "tag-pill") porque existe uma regra GLOBAL .tag-pill (background + cor +
   sublinhado) que se somava ao estilo inline de getTagStyle(). O estilo
   visual (cor/negrito p/ palavra única; apenas background p/ multipalavra)
   é definido inline por getTagStyle(); aqui ficam só as propriedades base. */
.subcategory-tag {
  display: inline;
  white-space: normal;
  -webkit-box-decoration-break: clone;
  box-decoration-break: clone;
  padding: 2px 6px;
  border-radius: 6px;
  font-weight: 500;
  margin-left: 0.35rem;
  margin-bottom: 0.35rem;
  text-decoration: none; /* reforço: anula sublinhado de qualquer regra global */
  overflow-wrap: anywhere;
  word-break: break-word;
}
.tag-link {
  cursor: pointer;
  border: none;
  outline: none;
  background: transparent;
  font-family: inherit;
  font-weight: inherit;
  text-decoration: none;
  transition: background-color 0.2s ease-in-out, transform 0.05s ease;
}
.tag-link:hover {
  filter: brightness(0.95);
}
.tag-link:active {
  transform: translateY(1px);
}
.tag-link:focus,
.tag-link:focus-visible {
  outline: none;
  box-shadow: none;
}
.card-footer {
  display: flex;
  flex-direction: column;
  gap: 7px;
  margin-top: auto;
  padding-top: 8px;
  border-top: 1px dashed var(--border-color);
}
.footer-context-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
}
.footer-bottom-row {
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 8px;
}
.meta-chip {
  display: inline-flex;
  align-items: flex-start;
  gap: 4px;
  padding: 1px 0;
  border-radius: 0;
  font-size: 0.7rem;
  font-weight: 500;
  background: transparent !important;
  border: none;
  opacity: 0.82;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}
.meta-chip-label {
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  line-height: 1.25;
}
.meta-chip-text {
  color: var(--text-muted);
}
.meta-chip-activity {
  color: #786145;
}
.meta-sep {
  font-size: 0.52rem;
  color: var(--text-light);
  margin-top: 2px;
}
.note-source {
  font-size: 0.71rem;
  color: var(--text-light);
  font-style: italic;
  display: grid;
  grid-template-columns: 12px minmax(0, 1fr);
  column-gap: 6px;
  align-items: start;
  width: 100%;
  min-width: 0;
}
.note-source > span {
  min-width: 0;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  text-align: left;
}
.footer-icon {
  font-size: 0.65rem;
  opacity: 0.75;
  line-height: 1.2;
  margin-top: 2px;
}
.empty-state {
  width: 100%;
  min-height: 180px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.65rem;

  /* ✅ PATCH: mesmo background geral do componente */
  background-color: var(--bg-content);

  color: var(--text-light);
  border: none;
  box-shadow: none;
  border-radius: 12px;
  text-align: center;
  padding: 2rem 1rem;
}

.empty-state p {
  margin: 0;
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text-muted);
}

.empty-state p {
  margin: 0;
  font-size: 0.9rem;
  color: var(--text-light);
}

.sub-pagination-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 0 0.25rem;
  font-size: 0.82rem;
  flex-shrink: 0;
}
.sub-pagination-left {
  display: flex;
  align-items: baseline;
  gap: 4px;
}
.sub-pagination-label {
  padding-top: 2px;
}

@media (max-width: 1200px) {
  .notes-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (max-width: 760px) {
  .notes-grid {
    grid-template-columns: 1fr;
  }
  .controls-bar,
  .sub-pagination-row {
    flex-direction: column;
    align-items: stretch;
  }
  .controls-right,
  .sub-pagination-left {
    justify-content: flex-start;
  }
  .filter-actions-right {
    width: 100%;
    margin-left: 0;
    justify-content: flex-start;
    flex-wrap: wrap;
  }
}
.tooltip-left-bottom {
  position: relative;
}
.tooltip-left-bottom::after {
  content: attr(data-tooltip);
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  background: var(--tooltip-bg);
  color: var(--tooltip-text);
  font-size: 0.68rem;
  font-weight: 500;
  line-height: 1;
  padding: 5px 8px;
  border-radius: 4px;
  white-space: nowrap;
  opacity: 0;
  transform: translateY(-2px);
  transition: opacity 0.15s ease, transform 0.15s ease;
  transition-delay: 1s;
  pointer-events: none;
  z-index: 80;
}
.tooltip-left-bottom:hover::after {
  opacity: 1;
  transform: translateY(0);
  transition-delay: 1s;
}

/* Desabilitado por permissão de vínculo — mantém pointer-events para tooltip */
.dropdown-item-disabled {
  opacity: 0.4;
  cursor: not-allowed !important;
  pointer-events: auto !important;
}
.dropdown-item-disabled:hover {
  background-color: transparent !important;
}

.btn-my-notes {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  padding: 0 0.8rem;
  font-size: 0.85rem;
  border-radius: 10px;
  border: 1px solid var(--border-color);
  background-color: var(--neutral-200);
  color: var(--text-dark);
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  white-space: nowrap;
  gap: 0.35rem;
}
.btn-my-notes.active {
  background-color: var(--brand-light);
  border-color: var(--brand-light);
  color: var(--text-on-brand);
}
.btn-my-notes.active:hover {
  background-color: var(--brand-light-hover);
}

.btn-my-notes:active {
  transition: background-color 0.2s ease-in-out, transform 0.05s ease;
  transform: translateY(1px);
}
.btn-my-notes i {
  font-size: 0.85rem;
}

.filter-actions-right {
  margin-left: auto;
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
  flex: 0 0 auto;
  min-width: max-content;
}
.filter-field-batch-actions {
  flex: 0 0 auto;
  min-width: unset;
  max-width: unset;
}
.btn-advanced.active {
  background-color: var(--brand-light);
  border-color: var(--brand-light);
  color: var(--text-on-brand);
}
.btn-advanced.active:hover {
  background-color: var(--brand-light-hover);
}
.advanced-count {
  display: inline-grid;
  place-items: center;
  box-sizing: border-box;
  width: 18px;
  height: 18px;
  padding: 0;
  border-radius: 999px;
  background-color: var(--text-on-brand-soft);
  color: #fff;
  font-size: 0.68rem;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
  flex-shrink: 0;
}
/* Paleta espelhada do card de metadados do texto (TextDisplayer.vue) */
.active-filters-panel {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  margin-top: 0.75rem;
  padding: 14px 18px 16px;
  border: 1px solid rgba(202, 138, 4, 0.22);
  border-radius: 14px;
  background: rgb(var(--surface-rgb) / 0.32);
  box-shadow: 0 2px 8px rgba(120, 53, 15, 0.06);
}
.active-filters-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: fit-content;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--text-dark);
}
.active-filters-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.active-filters-empty {
  margin: 0;
  font-size: 0.8rem;
  color: var(--text-light);
}
.filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: 32px;
  padding: 0 0.35rem 0 0.6rem;
  border: 1px solid var(--brand-soft-hover);
  /* mesmo raio do botão "Mais filtros" (.btn-batch) */
  border-radius: 10px;
  background-color: var(--brand-soft);
  color: var(--text-on-brand-soft);
}
.filter-chip--clickable {
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}
.filter-chip--clickable:hover {
  background-color: var(--brand-soft-hover);
  border-color: var(--brand-light);
}
.filter-chip--clickable:focus-visible {
  outline: 2px solid var(--brand);
  outline-offset: 1px;
}
.filter-chip-icon {
  width: 13px;
  text-align: center;
  font-size: 0.72rem;
  color: inherit;
  opacity: 0.75;
  flex-shrink: 0;
}
.filter-chip-label {
  font-size: 0.76rem;
  font-weight: 600;
  color: inherit;
  white-space: nowrap;
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
}
.filter-chip-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 50%;
  background-color: transparent;
  color: inherit;
  opacity: 0.65;
  font-size: 0.7rem;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease, opacity 0.15s ease;
}
.filter-chip-remove:hover {
  background-color: var(--danger-bg);
  color: var(--danger-text);
  opacity: 1;
}
.btn-add-filter {
  gap: 0.4rem;
}
.add-filter-menu {
  right: auto;
  left: 0;
  /* alto o bastante para caber a coluna inteira com campos abertos, sem rolagem */
  max-height: min(560px, calc(100vh - 160px));
  overflow-y: auto;
  padding: 0.4rem;
}
/* duas colunas independentes: abrir um campo cresce só a coluna dele,
   sem deslocar as opções da coluna vizinha */
.add-filter-grid {
  display: flex;
  align-items: stretch;
}
.add-filter-column {
  display: flex;
  flex-direction: column;
  align-content: flex-start;
  gap: 0.15rem;
  /* largura fixa: o menu não muda de tamanho ao abrir um campo */
  width: 330px;
  flex: 0 0 330px;
}
/* linha divisória entre as duas colunas */
.add-filter-column + .add-filter-column {
  border-left: 1px solid var(--border-color);
}
/* o campo abre sempre na linha de baixo, ocupando a largura da coluna */
.add-filter-option {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.3rem;
  padding: 0.25rem 0.7rem;
  border-radius: 8px;
  min-width: 0;
  cursor: pointer;
  transition: background-color 0.15s ease;
}
/* o checkbox é a própria marcação da opção: continua ao lado do nome */
.add-filter-option.is-checkbox {
  flex-direction: row;
  align-items: center;
  gap: 0.4rem;
}
/* hover na linha inteira, como as linhas de filtro de NoteFilter.vue */
.add-filter-option:hover {
  background-color: var(--brand-tint);
}
/* foco (navegação por setas) espelha o hover na linha inteira */
.add-filter-option:focus-within {
  background-color: var(--brand-tint);
}
.add-filter-name:focus {
  outline: none;
}
.add-filter-name {
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: 0.4rem;
  border: none;
  background: transparent;
  padding: 0.2rem 0.25rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-dark);
  text-align: left;
  white-space: nowrap;
  cursor: pointer;
  border-radius: 6px;
  transition: background-color 0.15s ease;
}
/* mesmo cinza dos ícones de NoteFilter.vue (.filter-icon) */
.add-filter-icon {
  width: 14px;
  text-align: center;
  font-size: 0.78rem;
  color: var(--text-subtle);
  flex-shrink: 0;
}
.add-filter-inputs {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  width: 100%;
  min-width: 0;
}
.add-filter-sublabel {
  font-size: 0.7rem;
  color: var(--text-light);
}
/* todos os campos com a mesma largura: a da coluna */
.add-filter-input {
  width: 100%;
  flex: 1 1 auto;
  min-width: 0;
  height: 26px;
  padding: 0 0.4rem;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background-color: var(--surface);
  color: var(--text-dark);
  font-size: 0.78rem;
  box-sizing: border-box;
}
.add-filter-input:focus {
  outline: none;
  border-color: var(--focus-border);
  box-shadow: var(--focus-ring);
}
/* o período divide a mesma largura entre as duas datas */
.add-filter-input-date {
  flex: 1 1 0;
  width: auto;
}
/* ✅ NOVO: linha de checkboxes do filtro "Tipo de observação" */
.add-filter-obs-types {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 12px;
  width: 100%;
  padding: 0.1rem 0.25rem 0.2rem;
}
/* Empurra o grupo "Leitura" para a direita e o separa dos tipos.
   O margin-left:auto vale por linha, então a quebra continua íntegra. */
.obs-group-divider {
  width: 1px;
  align-self: stretch;
  min-height: 14px;
  margin-left: auto;
  background-color: var(--border-color);
  flex-shrink: 0;
}
.obs-type-item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  font-size: 0.76rem;
  font-weight: 500;
  color: var(--text-dark);
  white-space: nowrap;
  user-select: none;
}
.obs-type-checkbox {
  width: 0.85rem;
  height: 0.85rem;
  border: 1px solid var(--text-subtle);
  border-radius: 3px;
  background-color: var(--surface);
  cursor: pointer;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  position: relative;
  margin: 0;
  flex-shrink: 0;
  transition: all 0.15s ease-in-out;
}
.obs-type-checkbox:hover {
  border-color: var(--type-color, var(--brand-light));
}
.obs-type-checkbox:checked {
  background-color: var(--type-color, var(--brand-light));
  border-color: var(--type-color, var(--brand-light));
}
.obs-type-checkbox:checked::after {
  content: "";
  position: absolute;
  left: 3.5px;
  top: 1px;
  width: 4px;
  height: 7px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}
.obs-type-checkbox:focus,
.obs-type-checkbox:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--type-color, var(--brand-light)) 35%, transparent);
}
.filter-secondary-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.75rem;
}
.page-size-control {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: 10px;
}
.page-size-input {
  width: 60px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid var(--border-color);
  padding: 0 6px;
  font-size: 0.8rem;
  text-align: center;
  background-color: var(--surface);
}
.page-size-input:focus {
  outline: none;
  border-color: var(--focus-border);
  box-shadow: var(--focus-ring);
}
.checkbox-all-label-disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.checkbox-all-label-disabled .checkbox-all-text {
  color: var(--text-subtle);
}
.checkbox-all-label-disabled:hover {
  cursor: not-allowed;
}
.checkbox-all-label-disabled .checkbox-style {
  cursor: not-allowed;
}
.checkbox-all-label-disabled .checkbox-style:hover {
  border-color: var(--text-subtle);
}
.btn-icon-share {
  background-color: var(--success-bg);
  color: var(--success-text);
}
.btn-icon-share:hover {
  background-color: var(--success-bg);
}
</style>